import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { stringify as toQueryString } from 'query-string';
import * as React from 'react';
import { NavLink, useHistory, useLocation, withRouter } from 'react-router-dom';
import { IContactSelection, ICreateCampaignRequest, ILocationState } from '../../../models';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { searchRequestToQueryStringParams } from '../../../models/UiUtils';
import { Page } from '../../components-v2/PageLayout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from '../../components/Breadcrumb';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { BuildingIcon } from '../../components/svgs/icons/BuildingIcon';
import { PeopleIcon } from '../../components/svgs/icons/PeopleIcon';
import { TagIcon } from '../../components/svgs/icons/TagIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../components/Tabs';
import { ROUTES } from '../../constants/routes';
import { CompaniesContainer } from '../../containers/PeopleV2/CompaniesContainer';
import { PeopleContainer } from '../../containers/PeopleV2/PeopleContainer';
import { DefaultPeopleFilter, DefaultPeopleSort } from '../../containers/PeopleV2/PeopleContainer/PeopleContext';
import { TagsContainer } from '../../containers/PeopleV2/TagsContainer';
import { baseStyleSheet } from '../../styles/styles';
import { styleSheet } from './styles';

enum TabValues {
	Companies = 'Companies',
	People = 'People',
	Tags = 'Tags',
}

export const Contacts = withRouter(
	observer(() => {
		const { logEvent } = useEventLogging();
		const location = useLocation();
		const userSession = useUserSession();
		const history = useHistory();
		const fullscreenModal = useFullscreenModal();

		const [tagAlertCommandObj, setTagAlertCommandObj] = React.useState<Api.IViewTagAlertContactsCommand>(null);
		const selectedTab = React.useMemo(() => {
			if (location.pathname.includes('companies')) {
				return TabValues.Companies;
			}
			if (location.pathname.includes('tags')) {
				return TabValues.Tags;
			}
			return TabValues.People;
		}, [location.pathname]);

		const onContactLoad = (filter: Api.IBulkContactsRequest, contacts: Api.IContact[]) => {
			if (
				tagAlertCommandObj &&
				filter.filter.criteria.find(x => x.property === Api.ContactFilterCriteriaProperty.TagAlert)
			) {
				if (tagAlertCommandObj.op === 'send') {
					if (contacts?.length > 0) {
						const contactViewModels = contacts.map(contact => new Api.ContactViewModel(userSession, contact));
						const locationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
							model: {
								context: {
									bulkFilterRequestOptions: {
										readOnlySearchCriteria: true,
									},
									contacts: contactViewModels,
								},
								type: 'Contacts',
							},
						};
						fullscreenModal?.history?.push({
							pathname: '/email/campaigns/create/from-selection',
							state: locationState,
						});
					}
				}

				// clear the tag alert command
				setTagAlertCommandObj(null);
			}
		};

		React.useEffect(() => {
			const queryCommand = Api.QueryCommands.People.parse(location.search);
			if (queryCommand && queryCommand.command === 'tagalert') {
				const tagAlertCommand = queryCommand as Api.IViewTagAlertContactsCommand;

				if (tagAlertCommand.id) {
					const tagAlert = new Api.TagAlertViewModel(userSession, {
						id: tagAlertCommand.id,
					});
					const promise = tagAlert.load();
					if (promise) {
						logEvent('TagAlertLoad', { id: tagAlertCommand.id });
						promise
							.then(() => {
								// create the initial filter request using the tag
								const filterRequest: Api.IContactsFilterRequest = {
									...DefaultPeopleFilter,
									criteria: [
										{
											property: Api.ContactFilterCriteriaProperty.TagAlert,
										},
										{
											property: Api.ContactFilterCriteriaProperty.Tag,
											value: tagAlert.tagValue,
										},
									],
								};
								const sortDescriptor: Api.ISortDescriptor = {
									...DefaultPeopleSort,
								};

								setTagAlertCommandObj(tagAlertCommand.op === 'send' ? tagAlertCommand : null);
								history.push(
									`${ROUTES.contacts.people}/?${toQueryString(searchRequestToQueryStringParams(filterRequest, sortDescriptor))}`
								);
							})
							.catch((error: Api.IOperationResultNoValue) => {
								logEvent('TagAlertLoad-Error', { ...error });
								history.push(location.pathname);
							});
					} else {
						history.push(location.pathname);
					}
				} else {
					const filterRequest: Api.IContactsFilterRequest = {
						...DefaultPeopleFilter,
						criteria: [
							{
								property: Api.ContactFilterCriteriaProperty.TagAlert,
							},
						],
					};

					const sortDescriptor: Api.ISortDescriptor = { ...DefaultPeopleSort };
					setTagAlertCommandObj(null);
					history.push(
						`${ROUTES.contacts.people}/?${toQueryString(searchRequestToQueryStringParams(filterRequest, sortDescriptor))}`
					);
				}
			} else if (queryCommand && queryCommand.command === 'tag') {
				const tagContactCommand = queryCommand as Api.IViewTagContactsCommand;
				if (tagContactCommand.id) {
					const filterRequest: Api.IContactsFilterRequest = {
						...DefaultPeopleFilter,
						criteria: [
							{
								property: Api.ContactFilterCriteriaProperty.Tag,
								value: tagContactCommand.id,
							},
						],
					};

					const sortDescriptor: Api.ISortDescriptor = { ...DefaultPeopleSort };
					setTagAlertCommandObj(null);
					history.push(
						`${ROUTES.contacts.people}/?${toQueryString(searchRequestToQueryStringParams(filterRequest, sortDescriptor))}`
					);
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const onSelectedTabChanged = (tabValue: string) => {
			const tab = TabValues[tabValue as keyof typeof TabValues];
			if (!tab) throw new Error('Invalid tab selected');

			if (tab === TabValues.Companies) return history.push(ROUTES.contacts.companies);
			if (tab === TabValues.People) return history.push(ROUTES.contacts.people);
			if (tab === TabValues.Tags) return history.push(ROUTES.contacts.tags);
		};

		const navLink = React.useMemo(() => {
			if (selectedTab === TabValues.Companies) return ROUTES.contacts.companies;
			if (selectedTab === TabValues.People) return ROUTES.contacts.people;
			if (selectedTab === TabValues.Tags) return ROUTES.contacts.tags;
		}, [selectedTab]);

		return (
			<Page>
				<MultiContainerHeader
					appBarHeader={
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to={navLink}>
											<h1 className={css(baseStyleSheet.breadcrumbTitle)}>{selectedTab}</h1>
										</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					}
				/>

				<TabRoot value={selectedTab} onValueChange={onSelectedTabChanged} className={css(styleSheet.tabRoot)}>
					<TabList styles={[styleSheet.tabList]}>
						<IconTabTrigger value={TabValues.People} icon={<PeopleIcon />}>
							People
						</IconTabTrigger>
						{userSession.account.preferences?.hideCompanies ||
						userSession.userRole === Api.EUserRole.LimitedUser ? null : (
							<IconTabTrigger value={TabValues.Companies} icon={<BuildingIcon />}>
								Companies
							</IconTabTrigger>
						)}
						{userSession.account.features.tags.canManageTags ? (
							<IconTabTrigger value={TabValues.Tags} icon={<TagIcon />}>
								Tags
							</IconTabTrigger>
						) : null}
					</TabList>
					<TabContent value={TabValues.People} className={css([styleSheet.tabContent])}>
						<PeopleContainer onContactsLoad={onContactLoad} />
					</TabContent>
					<TabContent value={TabValues.Companies} className={css([styleSheet.tabContent])}>
						<CompaniesContainer />
					</TabContent>
					<TabContent value={TabValues.Tags} className={css([styleSheet.tabContent])}>
						<TagsContainer />
					</TabContent>
				</TabRoot>
			</Page>
		);
	})
);
